import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const TimeBlock = ({ timeSlot, selectedDays, onChange, onDelete, isDefault }) => (
  <Paper sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <Typography variant="subtitle1">
        {isDefault ? 'Default Time Slot' : 'Additional Time Slot'}
      </Typography>
      {!isDefault && (
        <IconButton onClick={onDelete} color="error" size="small">
          <DeleteIcon />
        </IconButton>
      )}
    </Box>

    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <TextField
        label="Start Time"
        type="time"
        value={String(timeSlot.start).padStart(4, '0').replace(/(\d{2})(\d{2})/, '$1:$2')}
        onChange={(e) => {
          const value = e.target.value.replace(':', '');
          onChange({ ...timeSlot, start: parseInt(value) }, selectedDays);
        }}
        sx={{ width: 150 }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="End Time"
        type="time"
        value={String(timeSlot.end).padStart(4, '0').replace(/(\d{2})(\d{2})/, '$1:$2')}
        onChange={(e) => {
          const value = e.target.value.replace(':', '');
          onChange({ ...timeSlot, end: parseInt(value) }, selectedDays);
        }}
        sx={{ width: 150 }}
        InputLabelProps={{ shrink: true }}
      />
    </Box>

    <FormGroup row>
      {DAYS_OF_WEEK.map(day => (
        <FormControlLabel
          key={day}
          control={
            <Checkbox
              checked={selectedDays.includes(day)}
              onChange={(e) => {
                const newDays = e.target.checked
                  ? [...selectedDays, day]
                  : selectedDays.filter(d => d !== day);
                onChange(timeSlot, newDays);
              }}
            />
          }
          label={day.charAt(0).toUpperCase() + day.slice(1)}
          sx={{ width: 120 }}
        />
      ))}
    </FormGroup>
  </Paper>
);

const TimeSlotSelector = ({ onChange, initialOpeningHours = {} }) => {
  const [blocks, setBlocks] = useState(() => {
    // Initialize with default block
    const defaultBlock = {
      timeSlot: { start: 1600, end: 1900 },
      days: []
    };

    // Convert initial opening hours to blocks format if provided
    const existingBlocks = [];
    const processedTimes = new Set();

    DAYS_OF_WEEK.forEach(day => {
      (initialOpeningHours[day] || []).forEach(slot => {
        const timeKey = `${slot.start}-${slot.end}`;
        if (!processedTimes.has(timeKey)) {
          processedTimes.add(timeKey);
          existingBlocks.push({
            timeSlot: { start: slot.start, end: slot.end },
            days: [day]
          });
        } else {
          const blockIndex = existingBlocks.findIndex(block => 
            block.timeSlot.start === slot.start && block.timeSlot.end === slot.end
          );
          if (blockIndex !== -1) {
            existingBlocks[blockIndex].days.push(day);
          }
        }
      });
    });

    return existingBlocks.length > 0 ? existingBlocks : [defaultBlock];
  });

  const updateParent = (newBlocks) => {
    const newOpeningHours = DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {});

    newBlocks.forEach(block => {
      block.days.forEach(day => {
        newOpeningHours[day].push({
          start: block.timeSlot.start,
          end: block.timeSlot.end
        });
      });
    });

    onChange(newOpeningHours);
  };

  const handleBlockChange = (index, timeSlot, days) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { timeSlot, days };
    setBlocks(newBlocks);
    updateParent(newBlocks);
  };

  const handleAddBlock = () => {
    const newBlocks = [...blocks, { timeSlot: { start: 1600, end: 1900 }, days: [] }];
    setBlocks(newBlocks);
    updateParent(newBlocks);
  };

  const handleDeleteBlock = (index) => {
    if (index === 0) return; // Prevent deleting default block
    const newBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(newBlocks);
    updateParent(newBlocks);
  };

  return (
    <Box>
      {blocks.map((block, index) => (
        <TimeBlock
          key={index}
          timeSlot={block.timeSlot}
          selectedDays={block.days}
          onChange={(timeSlot, days) => handleBlockChange(index, timeSlot, days)}
          onDelete={() => handleDeleteBlock(index)}
          isDefault={index === 0}
        />
      ))}
      
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddBlock}
        variant="outlined"
        sx={{ mt: 1 }}
      >
        Add Time Slot
      </Button>
    </Box>
  );
};

export default TimeSlotSelector;